import axios from 'axios';
import { AddOrUpdateMemberToTagInput } from '@optimus/models';
import { Content } from '../common/models/content/Content';
import { GetContentRequest, GetContentRequestV4 } from '../common/models/content/GetContentRequest';
import { SubmitSurveyFormRequest } from '../common/models/content/SubmitSurveyFormRequest';
import { axiosInstance } from './apiConfig';
import { OptimizelyDecision } from '../common/hooks/useOptimizelyFlag';

export const getPersonalizedContent = async (
  userId: string,
  arn?: string,
  legacy?: boolean,
  promotionStrategy?: string,
  filterStrategy?: string,
  randomSeed?: number,
) => {
  const { data } = await axiosInstance.get(
    `/content/personalize/${userId}`,
    {
      params: {
        arn,
        legacy,
        promotionStrategy,
        filterStrategy,
        randomSeed,
      },
    },
  );
  return data;
};

export const getContent = async (request: GetContentRequest) => {
  const endpoint = '/content/v3';

  const params: Record<string, string | number | undefined> = {
    pageSize: request.pageSize,
    page: request.page,
    randomSeed: request.randomContentSeed,
  };

  if (request.algorithm) {
    params.algorithm = request.algorithm;
  }

  const { data } = await axiosInstance.get(endpoint, { params });

  return data;
};

export const getSavedContent = async (request: GetContentRequest) => {
  const { data } = await axiosInstance.get('/content/saved', {
    params: {
      pageSize: request.pageSize,
      page: request.page,
    },
  });
  return data;
};

export async function getPersonalizeContent(
  request: GetContentRequest,
  homeFeedPersonalizeDecision?: OptimizelyDecision,
): Promise<Content[]> {
  const promotionStrategy = homeFeedPersonalizeDecision
    ?.variables.promotionStrategy as (string | undefined);
  const filterStrategy = homeFeedPersonalizeDecision
    ?.variables.filterStrategy as (string | undefined);

  const { data } = await axiosInstance.get('/content/personalize', {
    params: {
      pageSize: request.pageSize,
      page: request.page,
      arn: undefined,
      promotionStrategy,
      filterStrategy: filterStrategy || 'filterUserTradesAndAllHomeService', // see FilterStrategies
    },
  });
  return data;
}

export async function getSearchContent(request: GetContentRequest): Promise<Content[]> {
  const { data } = await axiosInstance.get('/content/search', {
    params: {
      pageSize: request.pageSize,
      page: request.page,
      searchTerm: request.searchTerm,
      topic: request.topic,
      businessGoal: request.businessGoal,
      sortBy: request?.sortBy,
      sortOrder: request?.sortOrder,
    },
  });
  return data;
}

export const getContentById = async (contentId: number) => {
  const { data } = await axiosInstance.get(`/content/${contentId}`);
  return data;
};

export const getContentByTitle = async (title: string) => {
  const { data } = await axiosInstance.get(`/content/search?title=${encodeURIComponent(title)}`);
  return data;
};

export const getContentStatus = async (contentIds: string) => {
  const { data } = await axiosInstance.get(`/content/state?ids=${contentIds}`);
  return data;
};

export const getContentForm = async (formId: string | undefined) => {
  const { data } = await axiosInstance.get(`/forms/${formId}`);
  return data;
};

export const getFormAnswers =
  async (formId: string | undefined, memberId: string | undefined) => {
    const { data } = await axiosInstance.get('/answers', {
      params: {
        formId,
        memberId,
      },
    });
    return data;
  };

export const saveContentFormAnswers = async (request: SubmitSurveyFormRequest) => {
  const { data } = await axiosInstance.post('/answers', request);
  return data;
};

export const getTiktokEmbeddedCode = async (tiktokUrl: string) => {
  const { data } = await axios.get(`https://www.tiktok.com/oembed?url=${tiktokUrl}`);
  return data;
};

export const getPollResults = async (questionId: string | undefined) => {
  const { data } = await axiosInstance.get(`/polls/${questionId}/results`);
  return data;
};

export const getPollTotalVotes = async (questionId: string | undefined) => {
  const { data } = await axiosInstance.get(`/polls/${questionId}/totalVotes`);
  return data;
};

export const deletePollAnswer = async (answerId: string | undefined) => {
  const { data } = await axiosInstance.delete(`/answers/${answerId}`);
  return data;
};

export const addFollowTopic = async (
  memberId: string,
  request: AddOrUpdateMemberToTagInput,
) => {
  const { data } = await axiosInstance.post(`/members/${memberId}/tags`, request);
  return data;
};

export const deleteFollowTopic = async (memberId: string, tagId: string) => {
  const { data } = await axiosInstance.delete(`/members/${memberId}/tags/${tagId}`);
  return data;
};

export const getFollowTopics = async (memberId: string) => {
  const { data } = await axiosInstance.get(`/members/${memberId}/tags`);
  return data;
};

export const getTopTopics = async () => {
  const { data } = await axiosInstance.get('/content/top-topics');
  return data;
};

export const getContentByCategory = async (request: GetContentRequestV4) => {
  const { pageSize, page, ...categoryParam } = request;
  const { data } = await axiosInstance.get('/content/v4', {
    params: {
      pageSize,
      page,
      ...categoryParam,
    },
  });
  return data;
};
